import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { format, isBefore, isAfter, formatISO, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Button from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import { Day } from 'design-system/calendar/Day';
import { ButtonGroup } from 'design-system/ButtonGroup';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { Calendar } from 'design-system/calendar/Calendar';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import Config from 'utils/packages/configs';
import { useDateTimePicker } from 'hooks';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import TouchableBounce from 'utils/packages/TouchableBounce';
import RecurringTransactionsSwitch from 'features/transactions/components/RecurringTransactionsSwitch';
import { getLastNOccurrences, getNextOccurrence, getRRuleString, RRULE_FREQUENCY, } from 'features/subscriptions/utils/frequency';
import { RRule } from 'rrule';
import BottomPanel from 'design-system/BottomPanel';
import { isWeb } from '../../constants';
const combineDateandTime = (selected, time) => {
    const combinedDate = new Date(selected);
    combinedDate.setHours(time.getHours());
    combinedDate.setMinutes(time.getMinutes());
    combinedDate.setSeconds(0);
    combinedDate.setMilliseconds(0);
    return combinedDate;
};
const FORMAT = 'MMMM d, yyyy';
const LIMIT = 101;
const strings = {
    selectDate: 'Select a date',
    defaultButtonTitle: 'Next',
    recurring: 'Set as recurring',
};
export const SelectDateScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const isNotProd = Config?.IS_PROD === 'false';
    const isTransaction = route.params?.buttonText === 'Add transaction';
    const colors = useColors();
    const { preTitle, longTitle, subTitle, buttonText: buttonTextProps, } = route.params;
    const { minDate, maxDate, initialDate } = route.params.props || {};
    const { amount } = route.params;
    const { showRecurringSwitch } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: longTitle ? '' : strings.selectDate,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, longTitle]);
    const [selected, setSelected] = useState(initialDate ? new Date(initialDate) : new Date());
    const [time, setTime] = useState(new Date());
    const [buttonText, setButtonText] = useState(format(selected, FORMAT));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recurring, setRecurring] = useState(false);
    const [frequency, setFrequency] = useState(undefined);
    const [nextOccurrence, setNextOccurrence] = useState(null);
    const [pastTransactions, setPastTransactions] = useState(0);
    const [rRuleString, setRRuleString] = useState(undefined);
    useEffect(() => {
        if (frequency && selected) {
            const rule = new RRule({
                freq: RRULE_FREQUENCY[frequency].freq,
                interval: RRULE_FREQUENCY[frequency].interval,
                dtstart: selected,
            });
            setNextOccurrence(getNextOccurrence(rule));
            const pastOccurrences = getLastNOccurrences(rule, LIMIT);
            const pastCount = pastOccurrences.length;
            const adjustedCount = pastCount > 0 && isSameDay(pastOccurrences[0], new Date())
                ? pastCount - 1
                : pastCount;
            setPastTransactions(adjustedCount);
            setRRuleString(getRRuleString(rule));
        }
        else {
            setRRuleString(undefined);
            setNextOccurrence(null);
            setPastTransactions(0);
        }
    }, [frequency, selected]);
    const checkEnabled = useCallback((date) => {
        const currentDate = new Date(date.timestamp);
        if (!minDate && !maxDate) {
            return true;
        }
        if (minDate && maxDate) {
            return (isBefore(currentDate, maxDate) && isAfter(currentDate, minDate));
        }
        if (minDate) {
            return isAfter(currentDate, minDate);
        }
        return maxDate ? isBefore(currentDate, maxDate) : false;
    }, [maxDate, minDate]);
    const onDayPress = useCallback((date) => {
        if (!checkEnabled(date)) {
            return;
        }
        const utcDate = toZonedTime(date.timestamp, 'UTC');
        setSelected(utcDate);
        setButtonText(format(utcDate, FORMAT));
    }, [checkEnabled]);
    const { showDateTimePicker } = useDateTimePicker({
        initialValue: new Date(time),
        mode: 'time',
        onSubmitDate: (date) => {
            if (date !== undefined) {
                setTime(date);
            }
        },
    });
    // Dev only
    const timePicker = useMemo(() => isNotProd && !isWeb && isTransaction ? (<View style={styles.timePickerContainer}>
              <Text Text-16 Secondary centered>
                Transaction time
              </Text>
              <Spacer h={8}/>

              <TouchableBounce onPress={showDateTimePicker}>
                <View style={[
            styles.timePickerCard,
            { backgroundColor: colors.cards.onDark },
        ]}>
                  <Spacer h={16}/>
                  <Text Numbers-20>{format(time, 'HH:mm')}</Text>
                  <Spacer h={16}/>
                </View>
              </TouchableBounce>
            </View>) : null, [
        colors.cards.onDark,
        isNotProd,
        isTransaction,
        showDateTimePicker,
        time,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const onSubmit = useCallback(() => {
        if (isSubmitting)
            return;
        const utcDate = isNotProd && !isWeb && isTransaction
            ? new Date(combineDateandTime(selected, time).getTime() -
                combineDateandTime(selected, time).getTimezoneOffset() * 60000)
            : new Date(selected.getTime() - selected.getTimezoneOffset() * 60000);
        route.params.onSubmitDate(formatISO(utcDate), rRuleString, setIsSubmitting);
    }, [
        isNotProd,
        isTransaction,
        rRuleString,
        route.params,
        selected,
        time,
        isSubmitting,
    ]);
    const submitRecurringButton = useMemo(() => {
        const PastTransactionsText = () => pastTransactions > 0 && (<View style={styles.pastTransactionsContainer}>
              <Text TextThin-14 Gray centered>
                {pastTransactions > LIMIT - 2 ? '99+' : pastTransactions} past
                transaction
                {pastTransactions > 1 ? 's' : ''} will be created
              </Text>
            </View>);
        const SubmitButton = () => (<Button brand onPress={onSubmit} title={buttonTextProps || buttonText} isFetching={isSubmitting}/>);
        if (isWeb || !pastTransactions) {
            return (<ButtonGroup type="floating">
              <PastTransactionsText />
              <SubmitButton />
            </ButtonGroup>);
        }
        return (<BottomPanel>
            <PastTransactionsText />
            <SubmitButton />
          </BottomPanel>);
    }, [
        onSubmit,
        buttonTextProps,
        buttonText,
        pastTransactions,
        isSubmitting,
    ]);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView contentContainerStyle={paddingBottom} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            {!!longTitle && (<ScreenInfoHeader preTitle={preTitle} title={longTitle} subTitle={subTitle}/>)}
            <Calendar style={[styles.calendar, !longTitle && { marginTop: rem(8) }]} maxDate={maxDate} initialDate={initialDate} dayComponent={({ date }) => (<Day key={date?.dateString} date={date} isStartDay={date
                ? isSameDay(selected, toZonedTime(date.timestamp, 'UTC'))
                : false} onDayPress={onDayPress} isDisabled={date ? !checkEnabled(date) : false}/>)} blackMonthText/>
            {!!showRecurringSwitch && (<RecurringTransactionsSwitch recurring={recurring} setRecurring={setRecurring} frequency={frequency} setFrequency={setFrequency} amount={amount} nextOccurrence={nextOccurrence}/>)}
            {timePicker}
          </Animated.ScrollView>
          {recurring ? (submitRecurringButton) : (<ButtonGroup type="floating">
              <Button brand onPress={onSubmit} title={buttonTextProps || buttonText} isFetching={isSubmitting}/>
            </ButtonGroup>)}
        </AppView>);
}, ({ colors }) => ({
    title: isWeb ? 'Select a date' : undefined,
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    calendar: {
        ...(!isWeb && {
            alignSelf: 'center',
        }),
        width: '100%',
    },
    timePickerCard: {
        marginHorizontal: rem(16),
        borderRadius: rem(16),
        justifyContent: 'center',
        alignItems: 'center',
    },
    timePickerContainer: {
        marginTop: rem(24),
    },
    pastTransactionsContainer: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
    },
});
